// Mirrors iOS ShareTarget. Web equivalent of the project/track variants
// the share sheet, manage-access view, and composer all dispatch on. The
// CFs split per-target, so every owner-side call site has to pick the
// right name + path; this helper centralizes that so the UI components
// stay target-agnostic.
//
// Shape:
//   { kind: 'project' | 'track',
//     artistId, projectId,
//     trackId,                 // 'track' only
//     name, imageUrl,          // for header/art
//     subtitle }               // 'track': project name; 'project': null

import firebase from './firebaseConfig';

export const SHARE_HOST = 'https://open.demodeck.com';

export function projectTarget(project) {
    return {
        kind: 'project',
        artistId: project.artistId || project.artist?.id,
        projectId: project.id,
        trackId: null,
        name: project.name,
        imageUrl: project.imageUrl,
        subtitle: null,
    };
}

export function trackTarget(track) {
    const project = track.project;
    return {
        kind: 'track',
        artistId: project?.artist?.id || project?.artistId,
        projectId: project?.id,
        trackId: track.id,
        name: track.name,
        imageUrl: project?.imageUrl,
        subtitle: project?.name || null,
    };
}

export function entityRef(target) {
    const base = firebase.database().ref('artists').child(target.artistId)
        .child('projects').child(target.projectId);
    if (target.kind === 'track') {
        return base.child('tracks').child(target.trackId);
    }
    return base;
}

export function globalUrl(target, slug) {
    if (!slug) return null;
    return `${SHARE_HOST}/${target.kind === 'track' ? 't' : 'p'}/${slug}`;
}

// User-facing copy for the standard CF error variants the redeem flow can
// produce. Mirrors the table in SHARING_MODEL.md §10. Returns null for
// errors that should be handled before this is consulted (password-required
// and account-required gates have their own UI states).
export function shareLinkErrorMessage(err) {
    const code = err?.code;
    const msg = err?.message || '';
    if (code === 'functions/resource-exhausted' || code === 'resource-exhausted') {
        return 'Too many requests. Try again later.';
    }
    if (code === 'functions/permission-denied' || code === 'permission-denied') {
        if (msg === 'Link rotated.') return 'This link has been replaced.';
        if (msg === 'Link is no longer public.') return 'This is no longer shared.';
        return msg || 'You do not have access to this link.';
    }
    if (code === 'functions/not-found' || code === 'not-found') {
        return 'This link is no longer valid.';
    }
    return msg || 'Could not open this link.';
}

// CF wrappers ---------------------------------------------------------------

const fn = (name) => firebase.functions().httpsCallable(name);

// `mode` is `'public' | 'inviteOnly' | 'private'`. `inviteOnly` preserves
// existing grants + tokens; `private` cascade-wipes both.
export async function setAccessMode(target, mode, settings) {
    const data = {
        artistId: target.artistId,
        projectId: target.projectId,
        mode,
        settings,
    };
    if (target.kind === 'track') data.trackId = target.trackId;
    const cf = target.kind === 'track' ? 'setTrackAccessMode' : 'setProjectAccessMode';
    const res = await fn(cf)(data);
    return res?.data;
}

export async function resetGlobalLink(target) {
    const data = {
        artistId: target.artistId,
        projectId: target.projectId,
    };
    if (target.kind === 'track') data.trackId = target.trackId;
    const cf = target.kind === 'track' ? 'resetTrackGlobalLink' : 'resetProjectGlobalLink';
    const res = await fn(cf)(data);
    return res?.data;
}

export async function revokeViewer(target, viewerUid) {
    const data = {
        artistId: target.artistId,
        projectId: target.projectId,
        viewerUid,
    };
    if (target.kind === 'track') data.trackId = target.trackId;
    const cf = target.kind === 'track' ? 'revokeTrackViewer' : 'revokeProjectViewer';
    const res = await fn(cf)(data);
    return res?.data;
}
